import { useEffect, useState } from 'react'

import { useMutation } from '@apollo/client'
import { Tooltip } from '@mantine/core'

import { useAuth } from 'src/auth'
import { toast } from 'src/components/Overlays/Toast/Toast'
import { UPDATE_VOICE_AGENT } from 'src/graphql/voiceAgents.graphql'
import { isAdminOrOps } from 'src/lib/auth/permissions'

import SettingsSectionCard from '../../../components/SettingsSectionCard'

import QuestionTemplatesSection, { QuestionTemplate } from './QuestionsSection'

const QualificationQuestionsCard = ({ voiceAgent, refetchVoiceAgents }) => {
  const { currentUser } = useAuth()
  const isAdmin = isAdminOrOps(currentUser)

  const [editing, setEditing] = useState(false)
  const [questionTemplates, setQuestionTemplates] = useState<
    QuestionTemplate[]
  >(voiceAgent.questionTemplates || [])

  // Sync internal state with parent data changes only when not editing
  useEffect(() => {
    if (!editing) {
      setQuestionTemplates(voiceAgent.questionTemplates || [])
    }
  }, [voiceAgent, editing])

  const [updateVoiceAgent, { loading }] = useMutation(UPDATE_VOICE_AGENT, {
    onCompleted: () => {
      toast('Voice agent updated successfully', 'success')
      setEditing(false)
      refetchVoiceAgents()
    },
    onError: (error) => {
      toast(`Failed to update voice agent: ${error.message}`, 'error')
    },
  })

  const handleSave = () => {
    // Always send every field — including the technical ones hidden from
    // clients — so a client edit never drops variableName/variableType/etc.
    const transformedQuestionTemplates = questionTemplates.map((qt) => ({
      question: qt.question,
      variableName: qt.variableName,
      shortQuestionName: qt.shortQuestionName,
      variableType: qt.variableType,
      condition: qt.condition,
      disqualifiedReason: qt.disqualifiedReason,
    }))

    updateVoiceAgent({
      variables: {
        voiceAgentId: voiceAgent.voiceAgentId,
        input: { questionTemplates: transformedQuestionTemplates },
      },
    })
  }

  const handleCancel = () => {
    setQuestionTemplates(voiceAgent.questionTemplates || [])
    setEditing(false)
  }

  return (
    <SettingsSectionCard
      title={
        <div className="flex flex-row items-center gap-2">
          Qualification Questions
          <Tooltip label="Admin only">
            <div className="h-1 w-1 rounded-full bg-doubleNickel-error-400 text-xs" />
          </Tooltip>
        </div>
      }
      initialOpened={false}
      editable
      editing={editing}
      onEdit={() => setEditing(true)}
      onSave={handleSave}
      onCancel={handleCancel}
      loading={loading}
    >
      {/* Remount on edit/cancel so the section's local state resets to the
          latest saved templates. */}
      <QuestionTemplatesSection
        key={`${editing}-${voiceAgent.updatedAt}`}
        questionTemplates={questionTemplates}
        onQuestionTemplatesChange={setQuestionTemplates}
        disabled={!editing || loading}
        showTechnicalFields={isAdmin}
        canModifyQuestions={isAdmin}
      />
    </SettingsSectionCard>
  )
}

export default QualificationQuestionsCard
